/**
 * Treadmill de upgrades permanentes (PLANO-DIFERENCIAIS Fix 3 / PLANO-ECONOMIA-MONETIZACAO 5.1).
 * Sink estrutural de IdleCoin: curva geométrica `custo = base × 1.55ⁿ`, quatro trilhas.
 * Núcleo puro — sem DOM, sem I/O. Opera sobre um `state` genérico injetado pelo chamador.
 */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const GROWTH = 1.55;

/** Três trilhas permanentes. `levels` = nº de compras até o teto. */
export const TRACKS = Object.freeze({
  lootYield: Object.freeze({ id: 'lootYield', levels: 20, base: 3000 }),
  captureLuck: Object.freeze({ id: 'captureLuck', levels: 18, base: 2500 }),
  offlineCap: Object.freeze({ id: 'offlineCap', levels: 15, base: 5000 }),
});

export const TRACK_IDS = Object.freeze(Object.keys(TRACKS));

/**
 * Custo da n-ésima compra (0-indexado): a 1ª compra de uma trilha usa `level=0`.
 * Arredondado por trilha — cada degrau é um número inteiro estável em saves.
 */
export function cost(trackId, level) {
  const track = TRACKS[trackId];
  if (!track) return Infinity;
  const step = clamp(Math.floor(Number(level) || 0), 0, track.levels - 1);
  return Math.round(track.base * Math.pow(GROWTH, step));
}

/** Custo total para levar uma trilha do zero ao teto. */
export function totalCostToMax(trackId) {
  const track = TRACKS[trackId];
  if (!track) return 0;
  let total = 0;
  for (let level = 0; level < track.levels; level++) total += cost(trackId, level);
  return total;
}

/**
 * Soma das três trilhas maximizadas. Mantém o orçamento permanente documentado
 * sem transformar a cadência de combate em um upgrade global.
 */
export function totalCostToMaxAll() {
  return TRACK_IDS.reduce((sum, id) => sum + totalCostToMax(id), 0);
}

export function normalizeUpgrades(state) {
  if (!state || typeof state !== 'object') return state;
  const current = state.upgrades && typeof state.upgrades === 'object' ? state.upgrades : {};
  state.upgrades = {};
  for (const id of TRACK_IDS) {
    const raw = Math.floor(Number(current[id]) || 0);
    state.upgrades[id] = clamp(raw, 0, TRACKS[id].levels);
  }
  return state;
}

function currentLevel(state, trackId) {
  return clamp(Math.floor(Number(state?.upgrades?.[trackId]) || 0), 0, TRACKS[trackId]?.levels || 0);
}

/** Leitura/escrita de IdleCoin compatível com o state do app (`state.stats.coins`). */
function coinsOf(state) {
  if (state?.stats && typeof state.stats.coins === 'number') return state.stats.coins;
  return Number(state?.coins) || 0;
}

function setCoins(state, value) {
  if (state?.stats && typeof state.stats === 'object') state.stats.coins = value;
  else state.coins = value;
}

/** Custo da próxima compra de uma trilha; `null` se já estiver no teto. */
export function upgradeCost(trackId, currentLvl) {
  const track = TRACKS[trackId];
  if (!track) return null;
  const level = clamp(Math.floor(Number(currentLvl) || 0), 0, track.levels);
  if (level >= track.levels) return null;
  return cost(trackId, level);
}

export function canBuy(state, trackId) {
  const track = TRACKS[trackId];
  if (!track) return false;
  const level = currentLevel(state, trackId);
  if (level >= track.levels) return false;
  const price = cost(trackId, level);
  return coinsOf(state) >= price;
}

/**
 * Compra o próximo nível da trilha. Muta `state.upgrades` e o saldo de IdleCoin.
 * Retorno estável para a UI: `{ ok, cost, level, error }`.
 */
export function applyBuy(state, trackId) {
  const track = TRACKS[trackId];
  if (!state || typeof state !== 'object' || !track) {
    return { ok: false, cost: 0, error: 'invalid_track' };
  }
  normalizeUpgrades(state);
  const level = currentLevel(state, trackId);
  if (level >= track.levels) return { ok: false, cost: 0, error: 'maxed' };
  const price = cost(trackId, level);
  const balance = coinsOf(state);
  if (balance < price) return { ok: false, cost: price, error: 'insufficient_funds' };
  setCoins(state, balance - price);
  state.upgrades[trackId] = level + 1;
  return { ok: true, cost: price, level: level + 1 };
}

/**
 * Multiplicadores/efeitos derivados do nível de cada trilha.
 * - lootYield: +3%/nível de rendimento de drops.
 * - captureLuck: +1.5%/nível de chance de captura; auto-restock de bolas a partir do nível 8.
 * - offlineCap: +0.4h/nível no teto de progresso offline.
 */
export function upgradePerks(upgrades = {}) {
  const lootYieldLvl = clamp(Math.floor(Number(upgrades.lootYield) || 0), 0, TRACKS.lootYield.levels);
  const captureLuckLvl = clamp(Math.floor(Number(upgrades.captureLuck) || 0), 0, TRACKS.captureLuck.levels);
  const offlineCapLvl = clamp(Math.floor(Number(upgrades.offlineCap) || 0), 0, TRACKS.offlineCap.levels);

  return Object.freeze({
    lootYield: lootYieldLvl,
    captureLuck: captureLuckLvl,
    offlineCap: offlineCapLvl,
    // Compatibility field for older callers. Combat cadence belongs to the
    // Pokemon's stats/moves, never to a global account upgrade.
    combatIntervalFactor: 1,
    lootMultiplier: 1 + lootYieldLvl * 0.03,
    captureMultiplier: 1 + captureLuckLvl * 0.015,
    autoRestock: captureLuckLvl >= 8,
    offlineCapBonusHours: Math.round(offlineCapLvl * 0.4 * 100) / 100,
  });
}
